/**
 * Выгрузка карточек на маркетплейсы — чистая логика области действия.
 *
 * Здесь считается, что именно уедет: состав карточек, кабинеты и текст
 * предупреждения. Ни одно из трёх мест вызова не считает это самостоятельно —
 * иначе копии разойдутся, и оператор увидит одно, а сервер сделает другое.
 */

import {
  assertNever,
  MARKETPLACES,
  marketplaceSupports,
  normalizeMarketplace as normalizeMarketplaceCode,
} from '../../shared/marketplaces'
import type {
  PublishCabinetGroup,
  PublishBundlesSummary,
  PublishCardResult,
  PublishCardTarget,
  PublishCardsRequest,
  PublishJobLifecycle,
  PublishCatalogFreshness,
  PublishJobRecord,
  PublishJobSnapshot,
  PublishMarketplace,
  PublishMergeStatus,
  PublishPlan,
  PublishScopeKind,
} from './types'

/** Статус карточки, при котором её вообще можно выгрузить */
export const DRAFT_CARD_STATUS = 'draft'

/**
 * Подписи в этом модуле — полное имя площадки («Wildberries», «OZON»), не
 * короткий бейдж реестра («ВБ», «ОЗОН»). Берём `fullName` из общего профиля,
 * а не переизобретаем словарь: значения совпадают дословно с прежними.
 */
export const MARKETPLACE_LABEL: Record<string, string> = {
  wb: MARKETPLACES.wb.fullName,
  ozon: MARKETPLACES.ozon.fullName,
  ym: MARKETPLACES.ym.fullName,
}

export function marketplaceLabel(value: string): string {
  const key = String(value || '').toLowerCase()
  return MARKETPLACE_LABEL[key] || (value ? String(value).toUpperCase() : '—')
}

/**
 * Нормализация площадки: в БД встречается и 'wildberries', и 'WB'.
 *
 * Публикация подключена у площадок с `capabilities.publish` (ВБ, ОЗОН и, с
 * Этапа 2 SPEC_2026-09-13_YANDEX_MARKET.md §4.2, Я.Маркет) — решаем через
 * реестр, а не перечисляем коды здесь второй раз. Незнакомое реестру значение
 * пропускаем как есть, как и раньше делала эта функция, — решение, отправлять
 * ли карточку, принимают выше по стеку блоки выгрузки.
 */
export function normalizeMarketplace(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw) return ''
  const code = normalizeMarketplaceCode(raw)
  if (code && marketplaceSupports(code, 'publish')) return code
  return raw
}

/**
 * Экшен пересинхронизации каталога кабинета под свежесть, показанную в окне
 * задачи. `switch` с `assertNever` — третья площадка не должна молча уйти в
 * ветку ОЗОН (SPEC §3.1).
 */
function catalogSyncAction(marketplace: PublishMarketplace): PublishCatalogFreshness['sync_action'] {
  switch (marketplace) {
    case 'wb':
      return 'syncProductsFast'
    case 'ozon':
      return 'syncAllProducts'
    case 'ym':
      // Этап 1 SPEC_2026-09-13_YANDEX_MARKET.md — только полный обход каталога.
      return 'syncCatalog'
    default:
      return assertNever(marketplace)
  }
}

/** Карточка МП из любого из трёх экранов: поля называются одинаково */
export interface PublishCardLike {
  /** marketplace_cards.id во вкладках карточек PIM. */
  id?: number | null
  card_id?: number | null
  marketplace?: string | null
  seller_id?: number | null
  cabinet?: string | null
  seller_short_name?: string | null
  seller_name?: string | null
  status?: string | null
  /**
   * Явное состояние отправки — то самое, по которому решает сервер.
   *
   * Отдаёт его только список карточек PIM. У каталога товаров в строке
   * карточки этого поля нет, поэтому отсутствие значения переводит отбор на
   * прежнюю эвристику по `status` (см. `isPublishableCard`).
   */
  submit_state?: string | null
  /** nmID у WB, product_id у OZON. Непустой — карточка уже на площадке. */
  external_id?: string | null
  updated_at?: string | null
}

export function isDraftCard(card: PublishCardLike): boolean {
  return String(card?.status ?? '').trim().toLowerCase() === DRAFT_CARD_STATUS
}

/** Сколько «Отправляется» считается зависшим. Столько же в PUBLISHABLE_CARD_SQL. */
const STALE_CREATING_MS = 10 * 60 * 1000

/**
 * Карточку можно отправить: на площадке её ещё нет.
 *
 * Решение владельца 31.08.2026. Не «статус draft»: `error` до отправки и
 * `creating`, зависший после умершего прогона, — рабочие состояния черновика,
 * и повторять выгрузку по ним оператор должен уметь сам, без обнуления статуса
 * в базе. Дубля не будет: Wildberries и OZON отбивают второй товар с тем же
 * артикулом продавца.
 *
 * Правило зеркалит серверное (`PUBLISHABLE_CARD_SQL` в фоновой задаче и
 * `isPublishableCard` в плане): расхождение означало бы, что выбранная строка
 * молча выпадет из набора. Именно так и вышло 09.09.2026: 86 карточек кабинета
 * ВБ КСА показывались как «Ошибка», фронт клал их в набор по `status`, а сервер
 * отвергал по `submit_state = 'sent'` — оператор видел пустое окно выгрузки.
 *
 * Поэтому решает `submit_state`, когда экран его отдаёт: `not_sent` (наружу не
 * ходили), `rejected` (площадка отказала) и `sent` при пустом внешнем
 * идентификаторе (запрос ушёл, ответа не было — повтор разрешён владельцем
 * 09.09.2026). `in_flight` закрыт: запрос может быть прямо сейчас в полёте.
 *
 * Каталог товаров `submit_state` в строке карточки не отдаёт — там отбор
 * остаётся прежним, по `status`. Это единственное расхождение с сервером, и
 * оно безопасно: неподтверждённая отправка с того экрана в набор не попадёт,
 * но и молча не исчезнет — сервер вернёт её в «не поедут» с причиной.
 */
export function isPublishableCard(card: PublishCardLike): boolean {
  if (String(card?.external_id ?? '').trim()) return false
  const submitState = String(card?.submit_state ?? '').trim().toLowerCase()
  if (submitState) {
    return submitState === 'not_sent' || submitState === 'rejected' || submitState === 'sent'
  }
  const status = String(card?.status ?? '').trim().toLowerCase()
  if (status === DRAFT_CARD_STATUS || status === 'error') return true
  if (status !== 'creating') return false
  const updatedAt = card?.updated_at ? Date.parse(String(card.updated_at)) : NaN
  return Number.isFinite(updatedAt) && Date.now() - updatedAt > STALE_CREATING_MS
}

function cabinetName(card: PublishCardLike, sellerId: number | null): string {
  const name = card.cabinet || card.seller_short_name || card.seller_name
  if (name && String(name).trim()) return String(name).trim()
  return sellerId != null ? `Кабинет #${sellerId}` : 'Кабинет не определён'
}

/** Область действия пропускает карточку? */
export function scopeAcceptsMarketplace(scope: PublishScopeKind, marketplace: string): boolean {
  return scope === 'all' || normalizeMarketplace(marketplace) === scope
}

/**
 * Карточки товара → цели выгрузки. Берём только черновики и только те, что
 * проходят область действия: со страницы ВБ карточка OZON того же товара
 * в состав не попадает и в диалоге не считается.
 */
export function targetsFromCards(
  article: string,
  cards: PublishCardLike[] | null | undefined,
  scope: PublishScopeKind,
): PublishCardTarget[] {
  const cleanArticle = String(article ?? '').trim()
  if (!cleanArticle) return []
  return (cards ?? [])
    .filter((card) => card && isPublishableCard(card))
    .map((card) => {
      const marketplace = normalizeMarketplace(card.marketplace)
      const sellerId = Number.isFinite(Number(card.seller_id)) ? Number(card.seller_id) : null
      const rawCardId = card.id ?? card.card_id
      const cardId = Number.isInteger(Number(rawCardId)) && Number(rawCardId) > 0
        ? Number(rawCardId)
        : null
      return {
        ...(cardId !== null ? { cardId } : {}),
        article: cleanArticle,
        marketplace,
        sellerId,
        cabinet: cabinetName(card, sellerId),
      }
    })
    .filter((target) => scopeAcceptsMarketplace(scope, target.marketplace))
}

export function cabinetKey(target: {
  marketplace?: string | null
  sellerId?: number | null
  seller_id?: number | null
  cabinet?: string | null
}): string {
  const sellerId = target.sellerId ?? target.seller_id ?? null
  return `${normalizeMarketplace(target.marketplace)}|${sellerId ?? target.cabinet ?? 'unknown'}`
}

function targetKey(target: PublishCardTarget): string {
  return target.cardId != null
    ? `card:${target.cardId}`
    : `legacy:${target.article}|${cabinetKey(target)}`
}

/** Состав выгрузки: уникальные артикулы, карточки и кабинеты */
export function buildPublishPlan(
  targets: PublishCardTarget[],
  scope: PublishScopeKind,
): PublishPlan {
  const seenTargets = new Set<string>()
  const unique: PublishCardTarget[] = []
  for (const target of targets) {
    if (!target?.article) continue
    if (!scopeAcceptsMarketplace(scope, target.marketplace)) continue
    const key = targetKey(target)
    if (seenTargets.has(key)) continue
    seenTargets.add(key)
    unique.push(target)
  }

  const articles: string[] = []
  const seenArticles = new Set<string>()
  for (const target of unique) {
    if (seenArticles.has(target.article)) continue
    seenArticles.add(target.article)
    articles.push(target.article)
  }

  const cardIds: number[] = []
  const seenCardIds = new Set<number>()
  for (const target of unique) {
    const cardId = Number(target.cardId)
    if (!Number.isInteger(cardId) || cardId <= 0 || seenCardIds.has(cardId)) continue
    seenCardIds.add(cardId)
    cardIds.push(cardId)
  }

  const groups = new Map<string, PublishCabinetGroup>()
  for (const target of unique) {
    const key = cabinetKey(target)
    const group = groups.get(key)
    if (group) group.targets.push(target)
    else {
      groups.set(key, {
        key,
        marketplace: target.marketplace,
        sellerId: target.sellerId,
        cabinet: target.cabinet,
        targets: [target],
      })
    }
  }

  return {
    scope,
    articles,
    cardIds,
    targets: unique,
    cabinets: [...groups.values()],
    productCount: articles.length,
    cardCount: unique.length,
  }
}

/**
 * Предупреждение диалога. Текст зависит от области действия: оператор должен
 * понимать разницу, не открывая документацию.
 */
export function publishScopeWarning(scope: PublishScopeKind): string {
  const irreversible =
    'Выгрузка необратима: карточки уедут на боевые кабинеты маркетплейсов, ' +
    'отменить их одной кнопкой в PIM нельзя.'
  if (scope === 'wb') {
    return (
      `${irreversible} Со страницы Wildberries уезжают только карточки Wildberries. ` +
      'Карточки OZON этих товаров останутся черновиками.'
    )
  }
  if (scope === 'ozon') {
    return (
      `${irreversible} Со страницы OZON уезжают только карточки OZON. ` +
      'Карточки Wildberries этих товаров останутся черновиками.'
    )
  }
  if (scope === 'ym') {
    return (
      `${irreversible} Со страницы Я.Маркет уезжают только карточки Я.Маркета. ` +
      'Карточки Wildberries и OZON этих товаров останутся черновиками.'
    )
  }
  return (
    `${irreversible} Из каталога товаров уезжают все карточки выбранных товаров ` +
    'в статусе «Черновик» — на обеих площадках и во всех кабинетах сразу.'
  )
}

/** Подпись области для шапки окна статуса и подзаголовка диалога */
export function publishScopeLabel(scope: PublishScopeKind): string {
  if (scope === 'all') return 'Все площадки'
  return marketplaceLabel(scope)
}

/**
 * Тело запроса. У области 'all' поля marketplace нет вовсе — не пустая строка
 * и не undefined-ключ, а отсутствующий ключ.
 */
export function publishRequestBody(plan: PublishPlan): PublishCardsRequest {
  const marketplace = plan.scope === 'all'
    ? {}
    : { marketplace: plan.scope as PublishMarketplace }
  if (plan.cardIds.length > 0) {
    return {
      card_ids: [...plan.cardIds],
      publish_confirmed: true,
      ...marketplace,
    }
  }
  return {
    articles: [...plan.articles],
    publish_confirmed: true,
    ...marketplace,
  }
}

// ── Итоги по карточкам ──────────────────────────────────────────────

function textOrNull(value: unknown): string | null {
  const text = String(value ?? '').trim()
  return text ? text : null
}

/** Одна строка ответа сервера → строка окна статуса; мусор отбрасываем */
export function normalizePublishResult(raw: unknown): PublishCardResult | null {
  if (!raw || typeof raw !== 'object') return null
  const value = raw as Record<string, unknown>
  const rawCardId = value.card_id ?? (value as any).cardId
  const cardId = Number.isInteger(Number(rawCardId)) && Number(rawCardId) > 0
    ? Number(rawCardId)
    : null
  const article = textOrNull(value.article)
  if (!article && cardId === null) return null
  const sellerIdRaw = value.seller_id ?? (value as any).sellerId
  const sellerId = sellerIdRaw !== null && sellerIdRaw !== undefined && Number.isFinite(Number(sellerIdRaw))
    ? Number(sellerIdRaw)
    : null
  const rawImtId = Number(value.imt_id ?? (value as any).imtId)
  const imtId = Number.isSafeInteger(rawImtId) && rawImtId > 0 ? rawImtId : null
  const mergeStatuses: PublishMergeStatus[] = [
    'merged', 'skipped_disabled', 'failed', 'not_applicable',
  ]
  const rawMergeStatus = String(value.merge_status ?? (value as any).mergeStatus ?? '').trim()
  const mergeStatus = mergeStatuses.includes(rawMergeStatus as PublishMergeStatus)
    ? rawMergeStatus as PublishMergeStatus
    : null
  return {
    card_id: cardId,
    article,
    marketplace: textOrNull(normalizeMarketplace(value.marketplace)),
    seller_id: sellerId,
    cabinet: textOrNull(value.cabinet),
    status: String(value.status ?? '').trim() || 'unknown',
    code: textOrNull(value.code),
    error: textOrNull(value.error),
    merge_label: textOrNull(value.merge_label ?? (value as any).mergeLabel),
    imt_id: imtId,
    merge_status: mergeStatus,
  }
}

const RESULT_KEYS = ['cards', 'results', 'cardResults', 'card_results', 'items'] as const

/**
 * Итоги приходят вместе с фоновой задачей. Точное имя поля — за сервером,
 * поэтому принимаем любое из известных, но каждую строку валидируем.
 */
export function extractPublishResults(job: unknown): PublishCardResult[] {
  if (!job || typeof job !== 'object') return []
  const sources: unknown[] = []
  const value = job as Record<string, any>
  const metadata = value.metadata && typeof value.metadata === 'object' ? value.metadata : {}
  for (const key of RESULT_KEYS) {
    if (Array.isArray(metadata[key])) sources.push(...metadata[key])
    if (Array.isArray(value[key])) sources.push(...value[key])
  }
  const normalized = sources
    .map(normalizePublishResult)
    .filter((row): row is PublishCardResult => row !== null)
  return dedupeResults(normalized)
}

function resultKey(result: PublishCardResult): string {
  return result.card_id != null
    ? `card:${result.card_id}`
    : `legacy:${result.article ?? ''}|${cabinetKey(result)}`
}

/** Последнее слово за самым свежим итогом по той же карточке */
export function dedupeResults(results: PublishCardResult[]): PublishCardResult[] {
  const byKey = new Map<string, PublishCardResult>()
  for (const result of results) byKey.set(resultKey(result), result)
  return [...byKey.values()]
}

// ── Состояние самой задачи ──────────────────────────────────────────

const JOB_LIFECYCLE: PublishJobLifecycle[] = [
  'pending',
  'processing',
  'completed',
  'failed',
  'cancelled',
  'stopped',
]

/** Состояния, после которых задача уже ничего не пришлёт */
const TERMINAL_JOB_STATUSES = new Set<PublishJobLifecycle>([
  'completed',
  'failed',
  'cancelled',
  'stopped',
])

export function isTerminalJobStatus(status: string | null | undefined): boolean {
  return TERMINAL_JOB_STATUSES.has(String(status ?? '').trim().toLowerCase() as PublishJobLifecycle)
}

/** Подпись состояния задачи, когда своего шага она не назвала */
export const JOB_STATUS_LABEL: Record<PublishJobLifecycle, string> = {
  pending: 'Задача поставлена в очередь',
  processing: 'Выгрузка идёт',
  completed: 'Выгрузка завершена',
  failed: 'Задача завершилась отказом',
  cancelled: 'Выгрузка отменена',
  stopped: 'Задача остановлена',
  unknown: 'Состояние задачи неизвестно',
}

/**
 * Фоновая задача (из событий или из запроса состояния) → снимок для реестра.
 * Форма у обоих источников одна: их отдаёт один и тот же jobs-service.
 */
export function snapshotFromJob(job: unknown): PublishJobSnapshot | null {
  if (!job || typeof job !== 'object') return null
  const value = job as Record<string, any>
  const raw = String(value.status ?? '').trim().toLowerCase()
  const status: PublishJobLifecycle = (JOB_LIFECYCLE as string[]).includes(raw)
    ? (raw as PublishJobLifecycle)
    : 'unknown'
  const progress = value.progress && typeof value.progress === 'object' ? value.progress : {}
  const percentageRaw = Number(progress.percentage)
  const percentage = Number.isFinite(percentageRaw)
    ? Math.max(0, Math.min(100, Math.round(percentageRaw)))
    : 0
  const metadata = value.metadata && typeof value.metadata === 'object'
    ? value.metadata as Record<string, any>
    : {}
  const catalogFreshness: PublishCatalogFreshness[] = Array.isArray(metadata.catalog_freshness)
    ? metadata.catalog_freshness
      .map((row: any): PublishCatalogFreshness | null => {
        const code = normalizeMarketplaceCode(row?.marketplace)
        const sellerId = Number(row?.seller_id)
        if (!code || !marketplaceSupports(code, 'publish')
            || !Number.isInteger(sellerId) || sellerId <= 0) return null
        const marketplace = code as PublishMarketplace
        return {
          marketplace,
          seller_id: sellerId,
          checked_at: String(row?.checked_at ?? ''),
          last_success_at: textOrNull(row?.last_success_at),
          age_ms: Number.isFinite(Number(row?.age_ms)) ? Number(row.age_ms) : null,
          state: ['fresh', 'warning', 'stale', 'unknown'].includes(String(row?.state))
            ? row.state
            : 'unknown',
          warning_after_ms: Number(row?.warning_after_ms) || 0,
          hard_fail_after_ms: Number(row?.hard_fail_after_ms) || 0,
          blocks_real_publish: row?.blocks_real_publish === true,
          code: textOrNull(row?.code),
          sync_action: catalogSyncAction(marketplace),
        }
      })
      .filter((row: PublishCatalogFreshness | null): row is PublishCatalogFreshness => row !== null)
    : []
  const rawBundles = metadata.bundles && typeof metadata.bundles === 'object'
    ? metadata.bundles as Record<string, unknown>
    : null
  const bundles: PublishBundlesSummary | undefined = rawBundles
    ? {
      created: Math.max(0, Math.trunc(Number(rawBundles.created) || 0)),
      topped_up: Math.max(0, Math.trunc(Number(rawBundles.topped_up) || 0)),
      failed_cards: Math.max(0, Math.trunc(Number(rawBundles.failed_cards) || 0)),
    }
    : undefined
  return {
    status,
    currentStep: textOrNull(value.currentStep) || textOrNull(value.name),
    percentage,
    error: textOrNull(value.error),
    seenAt: new Date().toISOString(),
    ...(catalogFreshness.length ? { catalogFreshness } : {}),
    catalogFreshnessEnforced: metadata.catalog_freshness_enforced !== false,
    ...(bundles ? { bundles } : {}),
  }
}

export type PublishRowState = 'pending' | 'done' | 'error'

export interface PublishStatusRow {
  key: string
  cardId: number | null
  article: string
  marketplace: string
  cabinet: string
  sellerId: number | null
  /** Итоговый статус карточки; null — итога ещё нет */
  status: string | null
  code: string | null
  error: string | null
  state: PublishRowState
  mergeLabel: string | null
  imtId: number | null
  mergeStatus: PublishMergeStatus | null
}

function resultArticleLabel(result: PublishCardResult): string {
  return result.article || (result.card_id != null ? `Карточка #${result.card_id}` : 'Карточка')
}

function resultCabinetLabel(result: PublishCardResult): string {
  return result.cabinet ||
    (result.seller_id != null ? `Кабинет #${result.seller_id}` : 'Кабинет не определён')
}

export interface PublishStatusGroup {
  key: string
  marketplace: string
  cabinet: string
  sellerId: number | null
  rows: PublishStatusRow[]
}

/**
 * Отказ по карточке.
 *
 * Код или пояснение в строке итога — это всегда отказ, каким бы ни был статус.
 * Статус в строке — состояние КАРТОЧКИ, а не операции, и отказ, случившийся до
 * отправки карточки на площадку (не заполнена обязательная характеристика,
 * кабинет закрыт настройками, у товара нет черновиков), оставляет её в прежнем
 * состоянии: `draft`, `skipped`. Контракт сервера прямой: «успешная карточка
 * приходит с code: null и error: null», — поэтому непустой код означает
 * «сделано не то, о чём просили», и зелёным такая строка быть не может.
 */
export function rowState(
  status: string | null,
  code?: string | null,
  error?: string | null,
): PublishRowState {
  if (textOrNull(code) || textOrNull(error)) return 'error'
  if (!status) return 'pending'
  return status.toLowerCase() === 'error' ? 'error' : 'done'
}

/**
 * Строки окна статуса, сгруппированные по кабинету. До прихода итогов
 * показываем ожидаемый состав — пустое окно выглядело бы как «ничего не идёт».
 */
export function buildStatusGroups(record: PublishJobRecord | null): PublishStatusGroup[] {
  if (!record) return []
  const rows = new Map<string, PublishStatusRow>()
  const legacyTargetKeys = new Map<string, string>()

  for (const target of record.targets) {
    const key = targetKey(target)
    legacyTargetKeys.set(`legacy:${target.article}|${cabinetKey(target)}`, key)
    rows.set(key, {
      key,
      cardId: target.cardId ?? null,
      article: target.article,
      marketplace: target.marketplace,
      cabinet: target.cabinet,
      sellerId: target.sellerId,
      status: null,
      code: null,
      error: null,
      state: 'pending',
      mergeLabel: null,
      imtId: null,
      mergeStatus: null,
    })
  }

  for (const result of record.results) {
    const rawKey = resultKey(result)
    // Legacy article-job не возвращает card_id, хотя исходная строка UI уже
    // могла знать его. Совмещаем её с ожидаемой строкой по старой идентичности.
    const key = result.card_id == null ? legacyTargetKeys.get(rawKey) ?? rawKey : rawKey
    rows.set(key, {
      key,
      cardId: result.card_id,
      article: resultArticleLabel(result),
      marketplace: result.marketplace || '',
      cabinet: resultCabinetLabel(result),
      sellerId: result.seller_id,
      status: result.status,
      code: result.code ?? null,
      error: result.error ?? null,
      state: rowState(result.status, result.code, result.error),
      mergeLabel: result.merge_label ?? null,
      imtId: result.imt_id ?? null,
      mergeStatus: result.merge_status ?? null,
    })
  }

  const groups = new Map<string, PublishStatusGroup>()
  for (const row of rows.values()) {
    const key = cabinetKey(row)
    const group = groups.get(key)
    if (group) group.rows.push(row)
    else {
      groups.set(key, {
        key,
        marketplace: row.marketplace,
        cabinet: row.cabinet,
        sellerId: row.sellerId,
        rows: [row],
      })
    }
  }

  return [...groups.values()]
}

export interface PublishStatusTotals {
  total: number
  done: number
  failed: number
  pending: number
}

export function statusTotals(groups: PublishStatusGroup[]): PublishStatusTotals {
  const rows = groups.flatMap((group) => group.rows)
  return {
    total: rows.length,
    done: rows.filter((row) => row.state === 'done').length,
    failed: rows.filter((row) => row.state === 'error').length,
    pending: rows.filter((row) => row.state === 'pending').length,
  }
}

/**
 * Сколько товаров в выгрузке. Обычно это состав запуска; у записи,
 * восстановленной по итогам задачи, состава нет — считаем по строкам итога,
 * иначе окно, открытое из списка фоновых задач, показало бы «товаров: 0».
 */
export function recordArticleCount(record: PublishJobRecord | null): number {
  if (!record) return 0
  if (record.articles.length > 0) return record.articles.length
  return new Set(
    record.results
      .map((result) => result.article)
      .filter((article): article is string => Boolean(article)),
  ).size
}

/** Русское склонение для подписей вида «3 товара» */
export function plural(count: number, forms: [string, string, string]): string {
  const abs = Math.abs(count) % 100
  const tail = abs % 10
  if (abs > 10 && abs < 20) return forms[2]
  if (tail > 1 && tail < 5) return forms[1]
  if (tail === 1) return forms[0]
  return forms[2]
}
